import axios from 'axios';
import type {
  SagaInstanceListItem,
  SagaInstanceDetailResponse,
  SagaStepView,
  SagaSummaryResponse,
  SagaResumeResult,
  DemoTriggerRequest,
  DemoTriggerResponse,
  SagaInstanceFilter,
  PageResponse,
} from '../types/sagaMonitor';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// 요청 인터셉터 — Bearer 토큰 첨부
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// 응답 인터셉터 — ApiResponse<T> 언랩
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? 'Unknown error',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      'Network error';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

function buildFilterParams(filter: SagaInstanceFilter): URLSearchParams {
  const sp = new URLSearchParams();
  if (filter.sagaName) sp.set('sagaName', filter.sagaName);
  if (filter.status) sp.set('status', filter.status);
  if (filter.page !== undefined) sp.set('page', String(filter.page));
  if (filter.size !== undefined) sp.set('size', String(filter.size));
  if (filter.sort) sp.set('sort', filter.sort);
  return sp;
}

export const sagaMonitorApi = {
  /** GET /api/eraf/saga-monitor/instances — 사가 인스턴스 목록 (SAGA_MONITOR_READ) */
  listInstances: (filter: SagaInstanceFilter): Promise<PageResponse<SagaInstanceListItem>> => {
    const sp = buildFilterParams(filter);
    return apiClient
      .get<unknown, { data: PageResponse<SagaInstanceListItem> }>(
        `/saga-monitor/instances?${sp.toString()}`
      )
      .then((res) => res.data);
  },

  /** GET /api/eraf/saga-monitor/instances/{id} — 사가 인스턴스 상세 */
  getInstance: (id: number): Promise<SagaInstanceDetailResponse> =>
    apiClient
      .get<unknown, { data: SagaInstanceDetailResponse }>(`/saga-monitor/instances/${id}`)
      .then((res) => res.data),

  /** GET /api/eraf/saga-monitor/instances/{id}/steps — 사가 단계 타임라인 */
  getSteps: (id: number): Promise<SagaStepView[]> =>
    apiClient
      .get<unknown, { data: SagaStepView[] }>(`/saga-monitor/instances/${id}/steps`)
      .then((res) => res.data),

  /** POST /api/eraf/saga-monitor/instances/{id}/resume — 사가 재시작 (SAGA_MONITOR_RETRY) */
  resumeInstance: (id: number): Promise<SagaResumeResult> =>
    apiClient
      .post<unknown, { data: SagaResumeResult }>(`/saga-monitor/instances/${id}/resume`)
      .then((res) => res.data),

  /** POST /api/eraf/saga-monitor/demo/{sagaName} — 데모 사가 트리거 (SAGA_MONITOR_TRIGGER) */
  triggerDemo: (sagaName: string, body: DemoTriggerRequest): Promise<DemoTriggerResponse> =>
    apiClient
      .post<unknown, { data: DemoTriggerResponse }>(`/saga-monitor/demo/${sagaName}`, body)
      .then((res) => res.data),

  /** GET /api/eraf/saga-monitor/summary — 상태별 집계 */
  getSummary: (): Promise<SagaSummaryResponse> =>
    apiClient
      .get<unknown, { data: SagaSummaryResponse }>('/saga-monitor/summary')
      .then((res) => res.data),
};
